<script setup lang="ts">
    import type { RouteRecordRaw } from "vue-router"

    import BaseNavbarMenuItems from "@/components/Base/BaseNavbar/BaseNavbarMenuItems.vue"
    import { useUserStore } from "@/stores/user"
    import checkPermissions from "@/utils/checkPermissions"

    interface FormattedMenuItem {
        hasSubMenuGroup?: boolean
        subMenuGroup?: string
        item?: RouteRecordRaw
        subMenuGroupIndex?: string
        type?: string
        items?: RouteRecordRaw[]
    }

    defineProps<{
        items: RouteRecordRaw[]
    }>()

    const userStore = useUserStore()

    // subIndex 是 "1-1"、"2-10" 這種以 "-" 分隔的字串，要逐段轉數字比較，不能直接數字相減或字串排序
    function compareSubIndex(a?: string, b?: string): number {
        const partsA = (a ?? "").split("-").map(Number)
        const partsB = (b ?? "").split("-").map(Number)
        const length = Math.max(partsA.length, partsB.length)
        for (let i = 0; i < length; i++) {
            const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0)
            if (diff !== 0) return diff
        }
        return 0
    }

    function formatItems(items: RouteRecordRaw[]) {
        const groups: Record<string, { type: string; items: RouteRecordRaw[] }> = {}

        return (
            items?.reduce<FormattedMenuItem[]>((result, item) => {
                const meta = item.meta
                if (!meta) return result

                if (!meta.hasSubMenuGroup) {
                    result.push({
                        hasSubMenuGroup: meta.hasSubMenuGroup,
                        subMenuGroup: meta.subMenuGroup,
                        item: item,
                    })
                } else {
                    const subMenuGroup = meta.subMenuGroup || ""
                    if (!groups[subMenuGroup]) {
                        groups[subMenuGroup] = {
                            type: subMenuGroup,
                            items: [],
                        }
                        result.push({
                            subMenuGroupIndex: meta.subMenuGroupIndex,
                            hasSubMenuGroup: meta.hasSubMenuGroup,
                            subMenuGroup: subMenuGroup,
                            ...groups[subMenuGroup],
                        })
                    }
                    groups[subMenuGroup].items.push(item)
                }
                return result
            }, []) || []
        )
    }

    function getMenuItems(parentMenuItem: RouteRecordRaw, userStore: ReturnType<typeof useUserStore>) {
        return (
            parentMenuItem.children
                ?.filter((item) => item.meta?.isSubShow)
                .filter((item) => checkPermissions(item.meta?.permissions, userStore))
                .slice()
                .sort((a, b) => compareSubIndex(a.meta?.subIndex, b.meta?.subIndex)) || []
        )
    }
</script>
<template>
    <template v-if="items.length">
        <template v-for="(formatItem, index) of formatItems(items)">
            <el-menu-item
                v-if="!formatItem.hasSubMenuGroup || !formatItem.items || formatItem.items?.length === 0"
                :key="'item' + index"
                data-test="sidebar-item"
                :index="String(formatItem.item?.meta?.subIndex ?? '')"
                @click="() => formatItem.item && $router.push({ name: formatItem.item.name })"
            >
                <template #title>
                    {{ formatItem.item?.meta?.subGroupName }}
                </template>
            </el-menu-item>
            <el-sub-menu
                v-else
                :key="'group' + index"
                :index="String(formatItem.subMenuGroupIndex ?? '')"
                class="custom-sub-menu"
            >
                <template #title>
                    <span>{{ formatItem.subMenuGroup }}</span>
                </template>
                <el-menu-item-group>
                    <template v-for="(formatSubItem, subIndex) of formatItem.items" :key="subIndex">
                        <BaseNavbarMenuItems
                            v-if="getMenuItems(formatSubItem, userStore)"
                            :items="getMenuItems(formatSubItem, userStore)"
                        ></BaseNavbarMenuItems>
                    </template>
                </el-menu-item-group>
            </el-sub-menu>
        </template>
    </template>
</template>

<style scoped>
    .el-sub-menu.is-opened :deep(> .el-sub-menu__title > .el-sub-menu__icon-arrow) {
        @apply !rotate-0;
    }

    .el-sub-menu :deep(.el-sub-menu__icon-arrow) {
        @apply !-rotate-90;
    }

    :deep(.el-sub-menu__title .el-sub-menu__icon-arrow) {
        @apply right-[unset] left-2.5;
    }

    :deep(.custom-sub-menu.el-sub-menu.is-opened .el-sub-menu__title > span) {
        @apply opacity-50;
    }

    :deep(.custom-sub-menu.el-sub-menu .el-sub-menu__icon-arrow) {
        @apply hidden;
    }

    :deep(.el-menu-item-group__title) {
        padding: 0;
    }
</style>
